import {gql, useQuery} from '@apollo/client';
import {
  APICallState,
  AuthActions,
  MFAActions,
  PanelSeparator,
  ProfileInfoType,
  Selectors,
  StandardContainer,
  useIsDesktop,
  useIsLargeDesktop,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  CommonActions,
  RouteProp,
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {useDispatch, useSelector} from 'Provider';
import {View} from 'native-base';
import React, {useCallback, useEffect, useState} from 'react';
import {Platform} from 'react-native';
import GenericAPIErrorDisplay from 'components/Offers/GenericAPIErrorDisplay';
import PageTemplate from 'components/PageTemplate';
import PageTitle from 'components/PageTitle';
import RewardsHistory from 'components/RewardsHistory';
import ConnectedWinLossStatementsModule from 'containers/ConnectedWinLossModule';
import WinLossStatementsModule from 'components/WinLossStatementsModule';
import ConnectedPaymentsTab from 'containers/PaymentsTab';
import ConnectedProfileSummary from 'containers/ProfileSummary';
import ConnectedProfileTab from 'containers/ProfileTab';
import ConnectedSecurityTab from 'containers/SecurityTab';
import {usePageViewEvent} from 'hooks/usePageViewEvent';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {isWebViewSelector} from 'store/content';
import {
  PAYMENT_MODULE_STATE,
  PROFILE_TABS,
  ProfileActions,
} from 'store/profile/profile.reducer';
import {profileNavItemsSelector} from 'store/profile/profile.selectors';
import {useTheme} from 'styled-components/native';
import CommunicationsTab from 'containers/CommunicationsTab';

const QUERY = gql`
  query getMyRewardsKeyValueList {
    empire {
      myRewardsKeyValueList {
        ...WinLossStatementsModuleFragment
      }
    }
  }
  ${WinLossStatementsModule.fragments.maintenance}
`;

export type ParamsList = {
  ProfileScreen: {tab?: string; unlock?: string; msg?: string};
};

const contentRef = React.createRef();

export const scrollToContentTop = () => {
  // @ts-expect-error type
  contentRef.current?.scrollIntoView();
};

const ProfileScreen: React.FunctionComponent = () => {
  const isWeb = Platform.OS === 'web';
  const {data, error, loading} = useQuery(QUERY);
  const myRewardsKeyValue =
    loading || error ? undefined : data?.empire?.myRewardsKeyValueList[0];
  const widgetDispatch = useWidgetDispatch();
  const suppressHeaderFooter = useSelector(isWebViewSelector);

  const profileNavItems = useSelector(profileNavItemsSelector);

  const isDesktop = useIsDesktop();

  const guestProfileFailure = useWidgetState(
    Selectors.getProfileFailureSelector
  );
  const isRaaSToken = useWidgetState(Selectors.isRaaSTokenSelector) as boolean;
  const optinStatus = useWidgetState(
    Selectors.mfaOptinStatusSelector
  ) as boolean;
  const optinStatusState = useWidgetState(
    Selectors.mfaOptinStatusStateSelector
  ) as APICallState;
  const tokenValid = useWidgetState(Selectors.mfaTokenValidSelector) as boolean;
  const tokenValidationState = useWidgetState(
    Selectors.mfaTokenValidationStateSelector
  ) as APICallState;

  const isWebView = useSelector(isWebViewSelector);

  const dispatch = useDispatch();

  const theme = useTheme();
  const {athens} = theme.colors;

  const {trackPageViewEvent} = usePageViewEvent();
  const {trackAdobeEvent} = useTrackEvent();
  const mfaTokenValidationStatus = useWidgetState(
    Selectors.mfaTokenValidationStateSelector
  ) as APICallState;

  const retryGetGuestProfile = useCallback(() => {
    widgetDispatch(
      AuthActions.getGuestProfile({
        infoType: ProfileInfoType.allProfileInfo,
      })
    );
  }, [widgetDispatch]);

  const route = useRoute<RouteProp<ParamsList, 'ProfileScreen'>>();
  const navigation = useNavigation();

  const [activeTab, setActiveTab] = useState(
    PROFILE_TABS[(route?.params?.tab || '').toUpperCase()] ||
      PROFILE_TABS.PROFILE
  );

  const [pageIsTracked, setPageIsTracked] = useState(false);

  const isLargeDesktop = useIsLargeDesktop();

  useFocusEffect(trackPageViewEvent);

  // Retrieve data for the security profile tab
  useEffect(() => {
    if (isRaaSToken || !isWeb || isWebView) {
      widgetDispatch(MFAActions.getOptinStatus());
      widgetDispatch(MFAActions.getSecurityProfile());
    }
  }, [isRaaSToken, isWeb, isWebView, widgetDispatch]);

  useEffect(() => {
    dispatch(
      ProfileActions.updatePaymentModuleState(PAYMENT_MODULE_STATE.DEFAULT)
    );
  }, []);

  function IsSuccessOrFailCallState(callState: APICallState): boolean {
    return (
      callState === APICallState.SUCCESS ||
      callState === APICallState.INVALID_CODE ||
      callState === APICallState.ERROR
    );
  }

  useEffect(() => {
    // Log this page event if it has not already been Tracked and any associated calls have finished.
    if (
      !pageIsTracked &&
      IsSuccessOrFailCallState(optinStatusState) &&
      IsSuccessOrFailCallState(tokenValidationState)
    ) {
      trackAdobeEvent({
        event: AdobeEvent.pageLoad,
        page: {name: `my account ${activeTab.toLowerCase()}`},
      });
      setPageIsTracked(true);
    }
  }, [
    pageIsTracked,
    activeTab,
    trackAdobeEvent,
    optinStatusState,
    tokenValidationState,
    tokenValid,
    optinStatus,
  ]);

  useEffect(() => {
    widgetDispatch(MFAActions.validateToken());
  }, [activeTab, widgetDispatch]);

  useEffect(() => {
    if (route?.params?.tab && Platform.OS !== 'web') {
      setActiveTab(route?.params?.tab.toUpperCase());
    }
  }, [route?.params?.tab]);

  const setTabToProfile = () => {
    setActiveTab('PROFILE');
  };

  const renderAccountContent = () => {
    switch (activeTab) {
      case PROFILE_TABS.PROFILE:
        return <ConnectedProfileTab />;
      case PROFILE_TABS.SECURITY:
        return <ConnectedSecurityTab />;
      case PROFILE_TABS.PAYMENTS:
        return <ConnectedPaymentsTab setTabToProfile={setTabToProfile} />;

      case PROFILE_TABS.PREFERENCES:
        return <CommunicationsTab setTabToProfile={setTabToProfile} />;

      case PROFILE_TABS.STATEMENTS:
        return (
          <>
            <RewardsHistory />
            <PanelSeparator />
            <ConnectedWinLossStatementsModule
              maintenance={{
                isMaintenance: myRewardsKeyValue?.winLossStatementMaintenance,
                maintenanceEnd:
                  myRewardsKeyValue?.winLossStatementMaintenanceEnd?.timestamp,
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const updateTab = (tabName: string) => {
    if (route?.params?.tab) {
      navigation?.dispatch(
        CommonActions?.setParams({...route.params, tab: undefined})
      );
    }
    setPageIsTracked(false);
    setActiveTab(tabName);
  };

  return (
    <PageTemplate>
      <PageTitle
        activeTab={activeTab}
        title="MY ACCOUNT"
        navItems={profileNavItems}
        updateTab={updateTab}
        suppressHeaderFooter={suppressHeaderFooter}
      />
      <StandardContainer>
        {guestProfileFailure ? (
          <View mt={isDesktop ? 8 : 6} alignItems="center">
            <GenericAPIErrorDisplay onRetryPress={retryGetGuestProfile} />
          </View>
        ) : (
          <View
            ref={contentRef}
            flex={isDesktop ? undefined : 1}
            mt={isDesktop ? 8 : 6}
            flexDirection={isDesktop ? 'row' : 'column'}>
            {isDesktop && (
              <View flexDir="column" width="342px">
                <ConnectedProfileSummary />
              </View>
            )}
            {!isDesktop && activeTab === PROFILE_TABS.PROFILE && (
              <ConnectedProfileSummary />
            )}
            <View
              pl={isLargeDesktop ? 8 : isDesktop ? 5 : undefined}
              flexShrink={1}
              flex={1}
              borderTopColor={isDesktop ? undefined : athens}
              borderTopWidth={isDesktop ? undefined : 1}>
              {renderAccountContent()}
            </View>
          </View>
        )}
      </StandardContainer>
    </PageTemplate>
  );
};

export default ProfileScreen;
